import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, BookOpen, TrendingUp, LogOut, Heart, Home, MessageSquare, CreditCard, Calendar, Bell, GraduationCap, Video, ClipboardList, Star, FileText, Share2, Plus, CheckCircle, Clock, XCircle, Package, AlertTriangle, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';
import { CodeITTLogo } from '../components/CodeITTLogo';

import ImpersonationBanner from '../components/ImpersonationBanner';
import AnnouncementsBanner from '../components/AnnouncementsBanner';
import ParentMetricsRow from '../components/ParentMetricsRow';
import FeedbackTimeline from '../components/FeedbackTimeline';
import ProgressBadges from '../components/ProgressBadges';
import RightUtilityRail from '../components/RightUtilityRail';

const ParentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [children, setChildren] = useState([]);
  const [enrollments, setEnrollments] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [selectedChild, setSelectedChild] = useState(null);
  const [ptmDialogOpen, setPtmDialogOpen] = useState(false);
  const [ptmRequest, setPtmRequest] = useState({ child_id: '', message: '' });
  const [parentReports, setParentReports] = useState([]);
  const [viewingReport, setViewingReport] = useState(null);
  const [reportViewOpen, setReportViewOpen] = useState(false);
  const [childClassStats, setChildClassStats] = useState({});
  const [childFeedbacks, setChildFeedbacks] = useState({});
  const [viewingFeedback, setViewingFeedback] = useState(null);
  const [feedbackViewOpen, setFeedbackViewOpen] = useState(false);
  const FEEDBACK_LABELS = { focus_in_class: 'Focus in Class', project_completion: 'Project Completion', concept_understanding: 'Understanding Concepts', independence: 'Independence' };

  // Buy Classes
  const [classPackages, setClassPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [purchaseChild, setPurchaseChild] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [purchaseCurrency, setPurchaseCurrency] = useState('INR');
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [submittingPurchase, setSubmittingPurchase] = useState(false);
  const [childAllocations, setChildAllocations] = useState({});
  const [discountCode, setDiscountCode] = useState('');

  // Report Cards (new format)
  const [childReportCards, setChildReportCards] = useState([]);
  const [viewingRC, setViewingRC] = useState(null);

  // Discussions
  const [parentThreads, setParentThreads] = useState([]);
  const [selectedParentThread, setSelectedParentThread] = useState(null);
  const [parentNewMessage, setParentNewMessage] = useState('');

  // Parent Hub
  const [hubData, setHubData] = useState(null);
  const [hubLoading, setHubLoading] = useState(false);

  // Direct Buy
  const [buyNumClasses, setBuyNumClasses] = useState(8);
  const [buyCurrency, setBuyCurrency] = useState('INR');
  const [buyPaymentMethod, setBuyPaymentMethod] = useState('');
  const [buyChild, setBuyChild] = useState('');
  const [buySubmitting, setBuySubmitting] = useState(false);

  // Class Credits & Payments from Enrollment System
  const [childCredits, setChildCredits] = useState({});
  const [childPaymentHistory, setChildPaymentHistory] = useState({});

  const PAYMENT_METHODS = [
    { id: 'razorpay_card', label: 'Credit/Debit Card', sublabel: 'via Razorpay', icon: '💳', group: 'Razorpay' },
    { id: 'razorpay_upi', label: 'UPI', sublabel: 'via Razorpay', icon: '📱', group: 'Razorpay' },
    { id: 'paypal', label: 'PayPal', sublabel: 'International', icon: '🌐', group: 'PayPal' },
    { id: 'bank_transfer', label: 'Bank Transfer', sublabel: 'NEFT/IMPS/RTGS', icon: '🏦', group: 'Manual' },
  ];

  useEffect(() => {
    if (!user || user.role !== 'parent') {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [childrenRes, reportsRes] = await Promise.all([
        api.get('/parent/children'),
        api.get('/parent/reports').catch(() => ({ data: [] }))
      ]);
      setChildren(childrenRes.data);
      setParentReports(reportsRes.data);
      if (childrenRes.data.length > 0) {
        setSelectedChild(childrenRes.data[0]);
      }

      // Fetch enrollments for each child
      const enrollmentPromises = childrenRes.data.map(child =>
        api.get(`/parent/children/${child.id}/enrollments`)
          .then(res => ({ childId: child.id, data: res.data }))
      );
      
      const enrollmentResults = await Promise.all(enrollmentPromises);
      const enrollmentMap = {};
      enrollmentResults.forEach(result => {
        enrollmentMap[result.childId] = result.data;
      });
      setEnrollments(enrollmentMap);

      // Fetch class stats and feedbacks for each child
      const statsMap = {};
      const fbMap = {};
      for (const child of childrenRes.data) {
        try {
          const statsRes = await api.get(`/parent/children/${child.id}/class-stats`);
          statsMap[child.id] = statsRes.data;
        } catch {}
        try {
          const fbRes = await api.get(`/parent/children/${child.id}/class-feedbacks`);
          fbMap[child.id] = fbRes.data;
        } catch {}
      }
      setChildClassStats(statsMap);
      setChildFeedbacks(fbMap);

      // Fetch class credits from enrollment system for each child
      const creditMap = {};
      const payHistMap = {};
      for (const child of childrenRes.data) {
        try {
          const credRes = await api.get(`/parent/children/${child.id}/class-credits`);
          creditMap[child.id] = credRes.data;
        } catch {}
        try {
          const phRes = await api.get(`/parent/children/${child.id}/payment-history`);
          payHistMap[child.id] = phRes.data;
        } catch {}
      }
      setChildCredits(creditMap);
      setChildPaymentHistory(payHistMap);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleRequestPTM = async () => {
    if (!ptmRequest.child_id || !ptmRequest.message) {
      toast.error('Please select a child and enter a message');
      return;
    }
    try {
      await api.post('/parent/request-ptm', ptmRequest);
      toast.success('PTM request sent successfully!');
      setPtmDialogOpen(false);
      setPtmRequest({ child_id: '', message: '' });
    } catch (error) {
      toast.error('Failed to send PTM request');
    }
  };

  const getChildProgress = (childId) => {
    const childEnrollments = enrollments[childId] || [];
    if (childEnrollments.length === 0) return 0;
    return Math.round(childEnrollments.reduce((sum, e) => sum + e.progress, 0) / childEnrollments.length);
  };

  const fetchBuyClassesData = async () => {
    try {
      const [pkgRes, histRes] = await Promise.all([
        api.get('/parent/class-packages'),
        api.get('/parent/purchase-history')
      ]);
      setClassPackages(pkgRes.data);
      setPurchaseHistory(histRes.data);
    } catch {}
    // Fetch allocation for each child
    const allocMap = {};
    for (const child of children) {
      try {
        const res = await api.get(`/parent/children/${child.id}/allocation`);
        allocMap[child.id] = res.data;
      } catch {}
    }
    setChildAllocations(allocMap);
  };

  useEffect(() => {
    if (activeSection === 'buy-classes' && children.length > 0) fetchBuyClassesData();
    if (activeSection === 'discussions') fetchParentThreads();
    if (activeSection === 'reports') fetchChildReportCards();
    if (activeSection === 'payment-history') fetchBuyClassesData();
    if (selectedChild) fetchHubData(selectedChild.id);
  }, [activeSection, children, selectedChild]);

  const fetchParentThreads = async () => {
    try { const res = await api.get('/discussion-threads/mine'); setParentThreads(res.data); } catch {}
  };

  const fetchChildReportCards = async () => {
    try { const res = await api.get('/parent/all-report-cards'); setChildReportCards(res.data); } catch {}
  };

  const fetchHubData = async (studentId) => {
    if (!studentId) return;
    setHubLoading(true);
    try {
      const res = await api.get(`/parent/hub/${studentId}`);
      setHubData(res.data);
    } catch { setHubData(null); }
    setHubLoading(false);
  };

  const handleDirectPurchase = async () => {
    if (!buyChild) { toast.error('Select a child'); return; }
    if (buyNumClasses <= 0) { toast.error('Select number of classes'); return; }
    if (!buyPaymentMethod) { toast.error('Select payment method'); return; }
    setBuySubmitting(true);
    try {
      await api.post('/parent/buy-classes', {
        student_id: buyChild,
        num_classes: buyNumClasses,
        currency: buyCurrency,
        payment_method: buyPaymentMethod,
      });
      toast.success('Purchase request submitted! Admin will confirm shortly.');
      setBuyNumClasses(8);
      setBuyPaymentMethod('');
      fetchBuyClassesData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBuySubmitting(false);
  };

  const handleParentSendMessage = async () => {
    if (!parentNewMessage.trim() || !selectedParentThread) return;
    try {
      await api.post(`/teacher/discussion-threads/${selectedParentThread.id}/messages`, { message: parentNewMessage });
      setParentNewMessage('');
      const res = await api.get(`/discussion-threads/${selectedParentThread.id}`);
      setSelectedParentThread(res.data);
      fetchParentThreads();
    } catch { toast.error('Failed to send'); }
  };

  const handlePurchase = async () => {
    if (!purchaseChild) { toast.error('Please select a child'); return; }
    if (!selectedPackage) { toast.error('Please select a package'); return; }
    if (!paymentMethod) { toast.error('Please select a payment method'); return; }
    setSubmittingPurchase(true);
    try {
      await api.post('/parent/purchase-classes', {
        student_id: purchaseChild,
        package_id: selectedPackage.id,
        payment_method: paymentMethod,
        currency: purchaseCurrency,
        discount_code: discountCode,
      });
      toast.success('Purchase request submitted! Admin will confirm your payment shortly.');
      setSelectedPackage(null);
      setPaymentMethod('');
      setDiscountCode('');
      fetchBuyClassesData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit purchase'); }
    setSubmittingPurchase(false);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, color: 'text-amber-600 bg-amber-100' },
    { id: 'parent-hub', label: 'Parent Hub', icon: Heart, color: 'text-rose-600 bg-rose-100' },
    { id: 'progress', label: 'Student Progress', icon: TrendingUp, color: 'text-emerald-600 bg-emerald-100' },
    { id: 'buy-classes', label: 'Buy Classes', icon: Package, color: 'text-blue-600 bg-blue-100' },
    { id: 'payment-history', label: 'Payment History', icon: CreditCard, color: 'text-violet-600 bg-violet-100' },
    { id: 'reports', label: 'Report Cards', icon: ClipboardList, color: 'text-indigo-600 bg-indigo-100' },
    { id: 'discussions', label: 'Discussions', icon: MessageSquare, color: 'text-pink-600 bg-pink-100' },
    { id: 'notifications', label: 'Notifications', icon: Bell, color: 'text-orange-600 bg-orange-100' }
  ];

  const isImpersonating = localStorage.getItem('impersonating') === 'true';

  return (
    <div className={`min-h-screen flex v2 v2-app ${isImpersonating ? 'pt-10' : ''}`} data-testid="parent-dashboard">
      <ImpersonationBanner />
      {/* Left Sidebar */}
      <aside className="w-64 v2-sidebar fixed h-full">
        <div className="flex items-center gap-2.5 mb-4 px-2">
          <CodeITTLogo size="large" />
          <div>
            <h1 className="text-base font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', 'Manrope', sans-serif" }}>CodeITT</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-violet-600 -mt-0.5">Parent Portal</p>
          </div>
        </div>

        <nav className="flex-1 flex flex-col gap-1 overflow-y-auto pr-1">
          {sidebarItems.map((item) => {
            const active = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => item.action ? item.action() : setActiveSection(item.id)}
                className={`v2-nav-item w-full text-left ${active ? 'active' : ''}`}
                data-testid={`sidebar-${item.id}`}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}

          {/* Request PTM Button */}
          <Dialog open={ptmDialogOpen} onOpenChange={setPtmDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="w-full mt-3 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 text-violet-700 hover:from-violet-500/15 hover:to-fuchsia-500/15 border border-violet-200 font-bold text-sm transition-all"
                data-testid="request-ptm-btn"
              >
                <Video className="w-4 h-4" />
                <span>Request PTM</span>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle>Request Parent-Teacher Meeting</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Select Child</Label>
                  <Select value={ptmRequest.child_id} onValueChange={(val) => setPtmRequest({...ptmRequest, child_id: val})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select child" />
                    </SelectTrigger>
                    <SelectContent>
                      {children.map(child => (
                        <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Message / Reason</Label>
                  <Textarea
                    value={ptmRequest.message}
                    onChange={(e) => setPtmRequest({...ptmRequest, message: e.target.value})}
                    placeholder="Please describe the reason for the meeting..."
                    className="mt-1"
                    rows={4}
                  />
                </div>
                <Button onClick={handleRequestPTM} className="w-full bg-violet-600 hover:bg-violet-700">
                  Send Request
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </nav>

        <div className="pt-3 mt-3 border-t border-slate-200/60">
          <div className="flex items-center gap-3 px-2">
            <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-0 lg:mr-80 flex flex-col min-h-screen">
        <AnnouncementsBanner />
        {/* Top Bar with Logout */}
        <header className="sticky top-0 z-10 glass-topbar px-8 py-2.5 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800">{sidebarItems.find(i => i.id === activeSection)?.label || 'Dashboard'}</h2>
          <button onClick={handleLogout} className="flex items-center gap-2 px-5 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-all shadow-sm" data-testid="parent-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </header>
        <div className="p-8 admin-content">
        {/* Header */}
        <div className="ds-page-hero" data-testid="parent-page-hero">
          <h1>Welcome, {user?.name}</h1>
          <p>Monitor your children's progress and manage their education</p>
        </div>

        {/* Unified Parent-Hub Metrics — rendered across every section/phase */}
        {selectedChild && (
          <ParentMetricsRow
            metrics={hubData?.unified_metrics}
            loading={hubLoading}
            studentName={selectedChild.name}
          />
        )}

        {/* Dashboard Overview (hidden when parent-hub is active) */}
        {activeSection !== 'parent-hub' && (
        <>
        {/* Children Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {children.map((child) => (
            <div 
              key={child.id} 
              onClick={() => setSelectedChild(child)}
              className={`glass-card rounded-xl p-6 shadow-sm border-2 cursor-pointer transition-all ${
                selectedChild?.id === child.id ? 'border-amber-500' : 'border-black/15 hover:border-amber-300'
              }`}
              data-testid={`child-card-${child.id}`}
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                  <GraduationCap className="w-7 h-7 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 font-bold">{child.name}</h3>
                  <p className="text-sm text-slate-500">{child.email}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 font-semibold">Overall Progress</span>
                  <span className="font-medium text-slate-900 font-bold">{getChildProgress(child.id)}%</span>
                </div>
                <Progress value={getChildProgress(child.id)} className="h-2" />
                <div className="flex justify-between text-sm pt-2">
                  <span className="text-slate-600 font-semibold">Enrolled Courses</span>
                  <span className="font-medium text-slate-900 font-bold">{(enrollments[child.id] || []).length}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Selected Child Progress */}
        {selectedChild && (
          <div className="glass-card rounded-xl shadow-sm border border-black/15 overflow-hidden">
            <div className="p-6 border-b border-black/15 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 font-bold">{selectedChild.name}'s Progress</h2>
                    <p className="text-sm text-slate-600 font-semibold">Course-wise breakdown</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setActiveSection('discussions')}
                  data-testid="view-discussions-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  View Discussions
                </Button>
              </div>
            </div>

            {(enrollments[selectedChild.id] || []).length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No courses enrolled yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {(enrollments[selectedChild.id] || []).map((enrollment, idx) => (
                  <div key={idx} className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 font-bold">{enrollment.course_title}</h3>
                        <p className="text-sm text-slate-600 font-semibold">Teacher: {enrollment.teacher_name}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          enrollment.progress === 100 ? 'bg-emerald-100 text-emerald-700' :
                          enrollment.progress >= 50 ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700 font-semibold'
                        }`}>
                          {enrollment.progress === 100 ? 'Completed' : 'In Progress'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Progress value={enrollment.progress} className="flex-1 h-3" />
                      <span className="text-lg font-bold text-slate-900 font-bold w-16 text-right">
                        {enrollment.progress}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Class Credits from Enrollment System */}
        {selectedChild && childCredits[selectedChild.id] && childCredits[selectedChild.id].total_purchased > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 mt-4 shadow-sm" data-testid="parent-class-credits">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><CreditCard className="w-5 h-5 text-blue-600" /> Class Balance — {selectedChild.name}</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-xl"><p className="text-2xl font-black text-blue-700">{childCredits[selectedChild.id].total_purchased}</p><p className="text-xs text-blue-600 font-bold">Purchased</p></div>
              <div className="text-center p-3 bg-emerald-50 rounded-xl"><p className="text-2xl font-black text-emerald-700">{childCredits[selectedChild.id].classes_completed}</p><p className="text-xs text-emerald-600 font-bold">Completed</p></div>
              <div className="text-center p-3 bg-amber-50 rounded-xl"><p className="text-2xl font-black text-amber-700">{childCredits[selectedChild.id].classes_remaining}</p><p className="text-xs text-amber-600 font-bold">Remaining</p></div>
            </div>
            {childCredits[selectedChild.id].classes_remaining <= 2 && (
              <div className="mt-3 flex items-center gap-2 text-sm text-red-600 font-bold bg-red-50 rounded-lg px-3 py-2 border border-red-200" data-testid="parent-low-balance">
                <AlertTriangle className="w-4 h-4" /> Low balance! Only {childCredits[selectedChild.id].classes_remaining} class(es) remaining. Please renew.
              </div>
            )}
          </div>
        )}

        {/* Payment History for selected child */}
        {selectedChild && childPaymentHistory[selectedChild.id]?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 mt-4 shadow-sm" data-testid="parent-payment-history">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-600" /> Payment History — {selectedChild.name}</h3>
            <div className="space-y-2">
              {childPaymentHistory[selectedChild.id].map((p, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3 border border-slate-100">
                  <div>
                    <p className="text-sm font-bold text-slate-900">{p.currency} {p.amount?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">{p.num_classes} classes — {p.payment_method}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-600">{p.payment_date}</p>
                    <p className="text-[10px] text-slate-400 font-mono">{p.transaction_ref}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div 
            onClick={() => setActiveSection('buy-classes')}
            className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-shadow"
            data-testid="quick-buy-classes"
          >
            <CreditCard className="w-10 h-10 mb-4" />
            <h3 className="text-lg font-semibold">Buy Classes</h3>
            <p className="text-emerald-100 text-sm">Purchase class packages for your children</p>
          </div>
          
          <div 
            onClick={() => setActiveSection('discussions')}
            className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-shadow"
            data-testid="quick-discussions"
          >
            <MessageSquare className="w-10 h-10 mb-4" />
            <h3 className="text-lg font-semibold">Discussions</h3>
            <p className="text-violet-100 text-sm">View teacher-student conversations</p>
          </div>
          
          <div 
            onClick={() => setPtmDialogOpen(true)}
            className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-shadow"
            data-testid="quick-ptm"
          >
            <Video className="w-10 h-10 mb-4" />
            <h3 className="text-lg font-semibold">Request PTM</h3>
            <p className="text-amber-100 text-sm">Schedule a parent-teacher meeting</p>
          </div>
        </div>
        </>
        )}

        {/* PARENT HUB SECTION */}
        {activeSection === 'parent-hub' && (
          <div className="mt-8 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900" data-testid="parent-hub-title">Parent Hub</h2>
              <p className="text-slate-600 font-semibold">Complete overview of your child's learning journey</p>
            </div>

            {/* Child Selection */}
            {children.length > 1 && (
              <div className="flex gap-3 flex-wrap">
                {children.map(child => (
                  <button key={child.id} onClick={() => { setSelectedChild(child); fetchHubData(child.id); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full border-2 font-bold text-sm transition-all ${
                      selectedChild?.id === child.id ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-amber-300'
                    }`} data-testid={`hub-child-${child.id}`}>
                    <GraduationCap className="w-4 h-4" /> {child.name}
                  </button>
                ))}
              </div>
            )}

            {hubLoading ? (
              <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" /></div>
            ) : hubData ? (
              <>
                {/* Student Overview */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xl font-extrabold">
                      {hubData.student.name?.[0]}
                    </div>
                    <div>
                      <h3 className="text-xl font-extrabold text-[#0F172A]">{hubData.student.name}</h3>
                      <p className="text-sm font-bold text-[#1E40AF]">{hubData.student.citt_id}</p>
                    </div>
                    {hubData.badges && (
                      <div className="ml-auto flex items-center gap-1">
                        <span className="text-xs font-bold text-[#334155] bg-slate-100 px-3 py-1 rounded-full">
                          {hubData.badges.filter(b => b.earned).length}/{hubData.badges.length} Badges
                        </span>
                      </div>
                    )}
                  </div>
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
                      <p className="text-2xl font-extrabold text-blue-700">{hubData.credits.total_purchased}</p>
                      <p className="text-xs font-bold text-blue-500">Total Purchased</p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
                      <p className="text-2xl font-extrabold text-emerald-700">{hubData.credits.completed}</p>
                      <p className="text-xs font-bold text-emerald-500">Completed</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center border border-amber-100">
                      <p className="text-2xl font-extrabold text-amber-700">{hubData.credits.remaining}</p>
                      <p className="text-xs font-bold text-amber-500">Remaining</p>
                    </div>
                    <div className="bg-violet-50 rounded-xl p-4 text-center border border-violet-100">
                      <p className="text-2xl font-extrabold text-violet-700">{hubData.progress.percent}%</p>
                      <p className="text-xs font-bold text-violet-500">Progress</p>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-[#0F172A]">Curriculum Progress</span>
                    <span className="text-sm font-extrabold text-[#1E40AF]">{hubData.progress.completed_topics}/{hubData.progress.total_topics} topics</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3">
                    <div className="bg-gradient-to-r from-amber-400 to-orange-500 h-3 rounded-full transition-all duration-500" style={{ width: `${hubData.progress.percent}%` }} />
                  </div>
                </div>

                {/* Badges */}
                {hubData.badges && hubData.badges.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="hub-badges">
                    <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> Badges & Achievements</h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
                      {hubData.badges.map((badge, i) => (
                        <div key={i} className={`text-center p-3 rounded-xl border transition-all ${
                          badge.earned ? 'bg-amber-50 border-amber-200 shadow-sm' : 'bg-slate-50 border-slate-200 opacity-50'
                        }`} data-testid={`badge-${badge.id}`}>
                          <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-1.5 ${
                            badge.earned ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' : 'bg-slate-200 text-slate-400'
                          }`}>
                            {badge.icon === 'award' && <Star className="w-5 h-5" />}
                            {badge.icon === 'star' && <Star className="w-5 h-5" />}
                            {badge.icon === 'trophy' && <GraduationCap className="w-5 h-5" />}
                            {badge.icon === 'zap' && <TrendingUp className="w-5 h-5" />}
                            {badge.icon === 'code' && <BookOpen className="w-5 h-5" />}
                          </div>
                          <p className="text-[10px] font-bold text-[#0F172A] leading-tight">{badge.name}</p>
                          <p className="text-[9px] font-bold text-[#334155] mt-0.5">{badge.earned ? 'Earned' : 'Locked'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Upcoming Classes */}
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="hub-upcoming">
                    <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Calendar className="w-5 h-5 text-blue-500" /> Upcoming Classes</h3>
                    {hubData.upcoming_classes.length === 0 ? (
                      <p className="text-sm text-slate-400 py-3">No upcoming classes scheduled</p>
                    ) : (
                      <div className="space-y-2">
                        {hubData.upcoming_classes.slice(0, 5).map((cls, i) => (
                          <div key={i} className="flex items-center gap-3 bg-blue-50 rounded-lg px-3 py-2.5 border border-blue-100">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center"><Calendar className="w-4 h-4 text-blue-600" /></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-[#0F172A] truncate">{cls.topic_name || cls.course_name}</p>
                              <p className="text-xs text-[#334155]">{cls.date} at {cls.time} — {cls.teacher_name}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Quiz Stars */}
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="hub-quiz-stars">
                    <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Star className="w-5 h-5 text-yellow-500" /> Recent Quiz Performance</h3>
                    {hubData.quiz_stars.length === 0 ? (
                      <p className="text-sm text-slate-400 py-3">No quizzes completed yet</p>
                    ) : (
                      <div className="space-y-2">
                        {hubData.quiz_stars.slice(0, 5).map((q, i) => (
                          <div key={i} className="flex items-center justify-between bg-yellow-50 rounded-lg px-3 py-2.5 border border-yellow-100">
                            <div>
                              <p className="text-sm font-bold text-[#0F172A]">{q.quiz_name}</p>
                              <p className="text-xs text-[#334155]">{q.score}/{q.total} correct</p>
                            </div>
                            <div className="flex gap-0.5">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`w-4 h-4 ${s <= q.stars ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Curriculum Breakdown */}
                {hubData.curriculum && hubData.curriculum.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="hub-curriculum">
                    <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5 text-violet-500" /> Curriculum Breakdown</h3>
                    <div className="space-y-3">
                      {hubData.curriculum.map((c, i) => (
                        <div key={i} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-bold text-[#0F172A]">{c.name}</span>
                            <span className="text-xs font-bold text-[#1E40AF]">{c.completed}/{c.total} ({c.percent}%)</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div className="bg-gradient-to-r from-violet-400 to-indigo-500 h-2 rounded-full" style={{ width: `${c.percent}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Class Feedback Timeline */}
                {selectedChild && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="hub-feedback">
                    <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-orange-500" /> Class Feedback from Teachers</h3>
                    <FeedbackTimeline studentId={selectedChild.id} dataTestidPrefix="parent-timeline" />
                  </div>
                )}

                {/* Feedback-driven Progress Badges */}
                {selectedChild && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="hub-progress-badges">
                    <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> Progress Badges</h3>
                    <ProgressBadges studentId={selectedChild.id} dataTestidPrefix="parent-badges" compact />
                  </div>
                )}

                {/* CTA */}
                <div className="flex gap-3">
                  <button onClick={() => setActiveSection('buy-classes')} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-extrabold py-3 rounded-xl text-sm hover:shadow-lg transition-shadow" data-testid="hub-buy-classes">
                    Buy More Classes
                  </button>
                  <button onClick={() => setActiveSection('progress')} className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-extrabold py-3 rounded-xl text-sm hover:shadow-lg transition-shadow" data-testid="hub-view-progress">
                    View Detailed Progress
                  </button>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-10 text-center shadow-sm">
                <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-[#334155] font-bold">Select a child above to view their hub</p>
              </div>
            )}
          </div>
        )}

        {/* Reports Section */}
        {/* STUDENT PROGRESS SECTION */}
        {activeSection === 'progress' && (
          <div className="mt-8 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-bold">Student Progress</h2>
              <p className="text-slate-600 font-semibold">Class statistics and teacher feedback</p>
            </div>
            {children.map(child => {
              const stats = childClassStats[child.id];
              const feedbacks = childFeedbacks[child.id] || [];
              return (
                <div key={child.id} className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800 font-semibold">{child.name}</h3>

                  {/* Stats Cards */}
                  {stats ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="glass-card rounded-xl p-4 border text-center"><div className="text-2xl font-bold text-emerald-600">{stats.classes_completed}</div><div className="text-xs text-gray-500">Completed</div></div>
                        <div className="glass-card rounded-xl p-4 border text-center"><div className="text-2xl font-bold text-red-500">{stats.classes_missed}</div><div className="text-xs text-gray-500">Missed</div></div>
                        <div className="glass-card rounded-xl p-4 border text-center"><div className="text-2xl font-bold text-amber-600">{stats.classes_remaining}</div><div className="text-xs text-gray-500">Remaining</div></div>
                        <div className="glass-card rounded-xl p-4 border text-center"><div className="text-2xl font-bold text-teal-600">{stats.overall_percentage}%</div><div className="text-xs text-gray-500">Overall</div></div>
                      </div>

                      {/* Module Progress */}
                      {stats.modules?.length > 0 && (
                        <div className="glass-card rounded-xl border p-4">
                          <h4 className="font-semibold text-sm mb-3">Module Progress</h4>
                          {stats.modules.map((mod, i) => (
                            <div key={i} className="mb-3">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="font-medium">{mod.course_name} - {mod.category}</span>
                                <span className="text-teal-600 font-semibold">{mod.percentage}%</span>
                              </div>
                              <Progress value={mod.percentage} className="h-2" />
                              <div className="flex gap-4 mt-1 text-xs text-gray-400">
                                <span>{mod.completed} done</span>
                                <span>{mod.remaining} left</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="glass-card rounded-xl p-6 border text-center"><p className="text-gray-400 text-sm">No class data available yet</p></div>
                  )}

                  {/* Class Feedbacks */}
                  {feedbacks.length > 0 && (
                    <div className="glass-card rounded-xl border overflow-hidden">
                      <div className="px-4 py-3 border-b bg-slate-500/10"><h4 className="font-semibold text-sm">Class Feedback Reports</h4></div>
                      <div className="divide-y">
                        {feedbacks.map(fb => (
                          <div key={fb.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-500/10 cursor-pointer" onClick={() => { setViewingFeedback(fb); setFeedbackViewOpen(true); }} data-testid={`parent-fb-${fb.id}`}>
                            <div>
                              <p className="font-medium text-sm">{fb.class_title || `Class ${fb.class_number}`}</p>
                              <p className="text-xs text-gray-500">{fb.course_name} &middot; {fb.category}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-400 fill-amber-400" /><span className="font-bold text-sm">{fb.average_rating}/5</span></div>
                              <span className="text-xs text-gray-400">{new Date(fb.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Feedback View Dialog */}
            <Dialog open={feedbackViewOpen} onOpenChange={setFeedbackViewOpen}>
              <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-md">
                <DialogHeader><DialogTitle>Class Report</DialogTitle></DialogHeader>
                {viewingFeedback && (
                  <div className="space-y-4 mt-2">
                    <p className="text-sm text-gray-600">{viewingFeedback.class_title || `Class ${viewingFeedback.class_number}`} &middot; {viewingFeedback.course_name} &middot; Teacher: {viewingFeedback.teacher_name}</p>
                    <div className="flex items-center justify-between bg-teal-50 rounded-xl p-4 border border-teal-200">
                      <span className="font-semibold">Average Rating</span>
                      <div className="flex items-center gap-1"><Star className="w-5 h-5 text-amber-400 fill-amber-400" /><span className="text-xl font-bold text-teal-700">{viewingFeedback.average_rating}/5</span></div>
                    </div>
                    {Object.entries(FEEDBACK_LABELS).map(([key, label]) => (
                      <div key={key} className="flex items-center justify-between py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-700">{label}</span>
                        <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= (viewingFeedback.ratings?.[key] || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />)}</div>
                      </div>
                    ))}
                    <p className="text-xs text-gray-400 text-center">{new Date(viewingFeedback.created_at).toLocaleDateString()}</p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Buy Classes Section */}
        {activeSection === 'buy-classes' && (
          <div className="mt-8 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-bold">Buy Classes</h2>
              <p className="text-slate-600 font-semibold">Choose a child and number of classes to purchase</p>
            </div>

            {/* Child Selection */}
            {children.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {children.map(child => {
                  const alloc = childAllocations[child.id];
                  const isIndia = !child.timezone || ['IST', 'Asia/Kolkata', 'Asia/Calcutta'].includes(child.timezone);
                  return (
                    <div key={child.id} className={`glass-card rounded-xl p-5 border-2 transition-all cursor-pointer ${purchaseChild === child.id ? 'border-amber-500 ring-2 ring-amber-200' : 'border-black/15 hover:border-amber-300'}`}
                      onClick={() => { setPurchaseChild(child.id); setPurchaseCurrency(isIndia ? 'INR' : 'USD'); }} data-testid={`select-child-${child.id}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-emerald-600" /></div>
                        <div>
                          <p className="font-semibold text-slate-900 font-bold">{child.name}</p>
                          <p className="text-xs text-slate-500">{child.timezone || 'IST'} &middot; {isIndia ? '₹375/class' : '$15/class'}</p>
                        </div>
                        {purchaseChild === child.id && <CheckCircle className="w-5 h-5 text-amber-500 ml-auto" />}
                      </div>
                      {alloc ? (
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-emerald-500/10 rounded-lg p-2"><div className="text-lg font-bold text-emerald-600">{alloc.completed}</div><div className="text-[10px] text-gray-500">Done</div></div>
                          <div className="bg-amber-500/10 rounded-lg p-2"><div className="text-lg font-bold text-amber-600">{alloc.remaining}</div><div className="text-[10px] text-gray-500">Left</div></div>
                          <div className="bg-blue-500/10 rounded-lg p-2"><div className="text-lg font-bold text-blue-600">{alloc.total_assigned}</div><div className="text-[10px] text-gray-500">Total</div></div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">No classes allocated yet</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pricing & Class Count */}
            {purchaseChild && (() => {
              const child = children.find(c => c.id === purchaseChild);
              const isIndia = !child?.timezone || ['IST', 'Asia/Kolkata', 'Asia/Calcutta'].includes(child?.timezone);
              const ratePerClass = isIndia ? 375 : 15;
              const currency = isIndia ? 'INR' : 'USD';
              const symbol = isIndia ? '₹' : '$';
              const numClasses = Number(selectedPackage?.num_classes || 0);
              const total = ratePerClass * numClasses;
              return (
                <div className="glass-card rounded-2xl border-2 border-black/15 p-6 space-y-5">
                  <h3 className="font-bold text-lg text-slate-900 font-bold">Choose Number of Classes</h3>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                    <p className="text-sm text-blue-800 font-semibold">Rate: {symbol}{ratePerClass} per class {isIndia ? '(India)' : '(USA/Canada)'}</p>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[4, 8, 12, 16, 20, 24, 32, 48].map(n => (
                      <button key={n} onClick={() => setSelectedPackage({ id: `custom_${n}`, name: `${n} Classes`, num_classes: n, price_inr: isIndia ? 375 * n : 0, price_usd: isIndia ? 0 : 15 * n })}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${selectedPackage?.num_classes === n ? 'border-amber-500 bg-amber-500/10 ring-1 ring-amber-200' : 'border-black/15 hover:border-amber-300'}`}
                        data-testid={`class-count-${n}`}>
                        <div className="text-2xl font-black text-slate-900 font-bold">{n}</div>
                        <div className="text-xs text-slate-500">classes</div>
                        <div className="text-sm font-bold text-amber-600 mt-1">{symbol}{ratePerClass * n}</div>
                      </button>
                    ))}
                  </div>
                  {!isIndia && (
                    <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium">Contact the administrator to enable discounted packages.</p>
                    </div>
                  )}
                  {numClasses > 0 && (
                    <>
                      <h4 className="font-bold text-slate-900 font-bold">Payment Method</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {PAYMENT_METHODS.map(method => (
                          <button key={method.id} onClick={() => setPaymentMethod(method.id)}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${paymentMethod === method.id ? 'border-amber-500 bg-amber-500/10' : 'border-black/15 hover:border-amber-300'}`}
                            data-testid={`payment-${method.id}`}>
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{method.icon}</span>
                              <div><p className="font-semibold text-sm">{method.label}</p><p className="text-[10px] text-gray-500">{method.sublabel}</p></div>
                              {paymentMethod === method.id && <CheckCircle className="w-5 h-5 text-amber-500 ml-auto" />}
                            </div>
                          </button>
                        ))}
                      </div>
                      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-5 text-white">
                        <div className="flex justify-between mb-2"><span className="text-gray-300">Student</span><span className="font-semibold">{child?.name}</span></div>
                        <div className="flex justify-between mb-2"><span className="text-gray-300">Classes</span><span className="font-semibold">{numClasses}</span></div>
                        <div className="flex justify-between mb-2"><span className="text-gray-300">Rate</span><span className="font-semibold">{symbol}{ratePerClass}/class</span></div>
                        <div className="border-t border-gray-700 pt-3 mt-2 flex justify-between"><span className="text-lg font-bold">Total</span><span className="text-2xl font-black text-amber-400">{symbol}{total.toLocaleString()}</span></div>
                      </div>
                      <Button onClick={handlePurchase} disabled={submittingPurchase || !paymentMethod}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-slate-900 font-bold font-bold text-base py-6 rounded-xl" data-testid="submit-purchase-btn">
                        {submittingPurchase ? 'Submitting...' : 'Submit Purchase Request'}
                      </Button>
                      <p className="text-[10px] text-center text-gray-400">Payment will be verified by admin. Classes will be allocated upon confirmation.</p>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Purchase History */}
            {purchaseHistory.length > 0 && (
              <div className="glass-card rounded-xl border border-black/15 overflow-hidden">
                <div className="px-6 py-4 border-b border-black/10"><h3 className="font-bold text-slate-900 font-bold">Purchase History</h3></div>
                <div className="divide-y divide-slate-100">
                  {purchaseHistory.map(p => (
                    <div key={p.id} className="px-6 py-4 flex items-center justify-between" data-testid={`purchase-${p.id}`}>
                      <div>
                        <p className="font-medium text-sm text-slate-900 font-bold">{p.package_name} — {p.student_name}</p>
                        <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString()} &middot; {p.num_classes} classes &middot; {PAYMENT_METHODS.find(m => m.id === p.payment_method)?.label || p.payment_method}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-sm">{p.currency === 'INR' ? '₹' : '$'}{p.amount}</span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${p.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : p.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {p.status === 'confirmed' ? 'Confirmed' : p.status === 'pending' ? 'Pending' : 'Rejected'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeSection === 'reports' && (
          <div className="mt-8 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-bold">Report Cards</h2>
              <p className="text-slate-600 font-semibold">Report cards generated by teachers for your children</p>
            </div>

            {/* New Report Cards from teacher */}
            {viewingRC ? (
              <div className="glass-card rounded-xl border border-black/15 overflow-hidden shadow-sm" data-testid="parent-rc-view">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-white">Report Card</h2>
                      <p className="text-indigo-200 text-sm">CodeITT Academy &middot; {viewingRC.month_label}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-black font-bold">{viewingRC.student_name}</p>
                      <p className="text-indigo-200 text-xs">by {viewingRC.teacher_name}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-5">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-200">
                      <div className="text-3xl font-bold text-emerald-600">{viewingRC.total_classes_completed}</div>
                      <div className="text-xs text-slate-500 mt-1">Total Classes</div>
                    </div>
                    <div className="text-center p-4 bg-blue-500/10 rounded-xl border border-blue-200">
                      <div className="text-3xl font-bold text-blue-600">{viewingRC.classes_this_month}</div>
                      <div className="text-xs text-slate-500 mt-1">This Month</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-200">
                      <div className="text-3xl font-bold text-purple-600">{viewingRC.projects_completed?.length || 0}</div>
                      <div className="text-xs text-slate-500 mt-1">Projects</div>
                    </div>
                  </div>
                  {viewingRC.curriculum_names?.length > 0 && (
                    <div><p className="text-sm font-semibold text-slate-700 font-semibold mb-2">Curriculum</p>
                      <div className="flex flex-wrap gap-2">{viewingRC.curriculum_names.map((c, i) => <span key={i} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">{c}</span>)}</div>
                    </div>
                  )}
                  {viewingRC.projects_completed?.length > 0 && (
                    <div><p className="text-sm font-semibold text-slate-700 font-semibold mb-2">Projects Completed</p>
                      <div className="space-y-1">{viewingRC.projects_completed.map((p, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-slate-500/10 rounded-lg text-sm"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="font-medium">{p.title}</span><span className="text-xs text-slate-400">{p.course}</span></div>
                      ))}</div>
                    </div>
                  )}
                  <div className="p-4 bg-teal-50 rounded-xl border border-teal-200">
                    <p className="text-sm font-semibold text-teal-900 mb-1">Teacher Feedback</p>
                    <p className="text-slate-700 font-semibold text-sm">{viewingRC.teacher_feedback}</p>
                  </div>
                  {viewingRC.areas_of_improvement && (
                    <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-200">
                      <p className="text-sm font-semibold text-amber-900 mb-1">Areas of Improvement</p>
                      <p className="text-slate-700 font-semibold text-sm">{viewingRC.areas_of_improvement}</p>
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setViewingRC(null)} className="w-full">Back to List</Button>
                </div>
              </div>
            ) : childReportCards.length > 0 ? (
              <div className="space-y-3">
                {childReportCards.map(rc => (
                  <button key={rc.id} onClick={() => setViewingRC(rc)} className="w-full glass-card rounded-xl border border-black/15 p-4 text-left hover:border-amber-300 hover:shadow-md transition-all" data-testid={`parent-rc-${rc.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><ClipboardList className="w-5 h-5 text-indigo-600" /></div>
                        <div>
                          <p className="font-semibold text-slate-900 font-bold">{rc.student_name} — {rc.month_label}</p>
                          <p className="text-xs text-slate-500">by {rc.teacher_name} &middot; {rc.total_classes_completed} classes</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">{new Date(rc.generated_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-xl p-12 text-center border border-black/15 shadow-sm">
                <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No report cards available yet</p>
                <p className="text-xs text-slate-400 mt-1">Teachers will share report cards here</p>
              </div>
            )}

            {/* Legacy Progress Reports */}
            {parentReports.length > 0 && !viewingRC && (
              <div className="space-y-4 mt-6">
                <h3 className="font-semibold text-slate-700 font-semibold">Progress Reports</h3>
                {parentReports.map(report => (
                  <div key={report.id} onClick={async () => { try { const res = await api.get(`/report/${report.id}`); setViewingReport(res.data); setReportViewOpen(true); } catch { toast.error('Failed'); } }}
                    className="glass-card rounded-xl p-5 border border-black/15 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid={`parent-report-${report.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><ClipboardList className="w-5 h-5 text-emerald-600" /></div>
                        <div><h3 className="font-semibold text-slate-900 font-bold">{report.student_name}</h3><p className="text-xs text-slate-500">{new Date(report.generated_at).toLocaleDateString()}</p></div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${report.overall_rating === 'Excellent' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{report.overall_rating}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payment History Section */}
        {activeSection === 'payment-history' && (
          <div className="mt-8 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-bold">Payment History</h2>
              <p className="text-slate-600 font-semibold">All your purchase transactions</p>
            </div>
            {purchaseHistory.length === 0 ? (
              <div className="glass-card rounded-xl p-12 text-center border border-black/15">
                <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No payment history yet</p>
                <p className="text-xs text-slate-400 mt-1">Your purchases will appear here</p>
              </div>
            ) : (
              <div className="glass-card rounded-xl border border-black/15 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-500/10">
                      <TableHead>Date</TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Classes</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchaseHistory.map(p => (
                      <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                        <TableCell className="text-sm">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="font-medium">{p.package_name}</TableCell>
                        <TableCell>{p.student_name}</TableCell>
                        <TableCell className="text-center">{p.num_classes}</TableCell>
                        <TableCell className="font-semibold">{p.currency === 'INR' ? '₹' : '$'}{p.amount?.toLocaleString()}</TableCell>
                        <TableCell className="text-sm">{PAYMENT_METHODS.find(m => m.id === p.payment_method)?.label || p.payment_method}</TableCell>
                        <TableCell>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${p.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : p.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                            {p.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {/* Discussions Section */}
        {activeSection === 'discussions' && (
          <div className="mt-8 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-bold">Discussions</h2>
              <p className="text-slate-600 font-semibold">Teacher-student conversations for your children</p>
            </div>
            {selectedParentThread ? (
              <div className="glass-card rounded-xl border border-black/15 shadow-sm">
                <div className="p-4 border-b border-black/10">
                  <button onClick={() => setSelectedParentThread(null)} className="text-xs text-blue-600 hover:underline mb-1">&larr; Back to threads</button>
                  <h3 className="font-semibold text-slate-900 font-bold">{selectedParentThread.subject}</h3>
                  <p className="text-xs text-slate-500">{selectedParentThread.teacher_name} &middot; {selectedParentThread.student_name}</p>
                </div>
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto" data-testid="parent-thread-messages">
                  {(selectedParentThread.messages || []).map(msg => (
                    <div key={msg.id} className={`flex ${msg.sender_role === 'parent' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${msg.sender_role === 'parent' ? 'bg-amber-500/100 text-white' : msg.sender_role === 'teacher' ? 'bg-teal-100 text-teal-900' : 'bg-blue-100 text-blue-900'}`}>
                        <p className="text-xs opacity-70 mb-0.5">{msg.sender_name} ({msg.sender_role})</p>
                        <p>{msg.content}</p>
                        <p className="text-[10px] opacity-50 mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-black/10 flex gap-2">
                  <Input value={parentNewMessage} onChange={e => setParentNewMessage(e.target.value)} placeholder="Type a message..." className="flex-1" data-testid="parent-thread-input" onKeyDown={e => e.key === 'Enter' && handleParentSendMessage()} />
                  <Button onClick={handleParentSendMessage} className="bg-amber-500/100 hover:bg-amber-600" data-testid="parent-send-msg-btn">Send</Button>
                </div>
              </div>
            ) : (
              <>
                {parentThreads.length === 0 ? (
                  <div className="glass-card rounded-xl border border-black/15 p-12 text-center">
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No discussion threads yet</p>
                    <p className="text-xs text-slate-400 mt-1">Conversations between teachers and your children will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {parentThreads.map(t => (
                      <button key={t.id} onClick={async () => { try { const r = await api.get(`/discussion-threads/${t.id}`); setSelectedParentThread(r.data); } catch { setSelectedParentThread(t); } }} className="w-full glass-card rounded-xl border border-black/15 p-4 text-left hover:border-amber-300 transition-colors" data-testid={`parent-thread-${t.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-900 font-bold">{t.subject}</p>
                            <p className="text-sm text-slate-500">{t.teacher_name} &rarr; {t.student_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400">{(t.messages || []).length} messages</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Report View Dialog */}
        <Dialog open={reportViewOpen} onOpenChange={setReportViewOpen}>
          <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
            {viewingReport && (
              <>
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 -m-6 mb-6 p-8 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-black">Progress Report Card</h2>
                      <p className="text-amber-100 text-sm mt-1">CodeITT Academy</p>
                    </div>
                    <div className="text-right">
                      <p className="text-black font-bold text-lg">{viewingReport.student_name}</p>
                      <p className="text-amber-200 text-xs">{new Date(viewingReport.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                  </div>
                </div>

                {/* Overall Rating */}
                <div className="flex items-center justify-between p-4 rounded-xl border-2 border-black/15 mb-6" data-testid="parent-report-overall">
                  <div>
                    <p className="text-sm text-slate-500">Overall Performance</p>
                    <p className="text-xl font-bold text-slate-900 font-bold">{viewingReport.overall_rating}</p>
                  </div>
                  <div className={`px-5 py-2.5 rounded-xl text-xl font-bold ${
                    viewingReport.overall_rating === 'Excellent' ? 'bg-emerald-100 text-emerald-700' :
                    viewingReport.overall_rating === 'Good' ? 'bg-blue-100 text-blue-700' :
                    viewingReport.overall_rating === 'Satisfactory' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {viewingReport.overall_rating === 'Excellent' ? 'A' : viewingReport.overall_rating === 'Good' ? 'B' : viewingReport.overall_rating === 'Satisfactory' ? 'C' : 'D'}
                  </div>
                </div>

                {/* Curriculum Progress */}
                {viewingReport.curriculum_summary?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-slate-900 font-bold mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-amber-600" /> Curriculum Progress</h3>
                    {viewingReport.curriculum_summary.map((c, i) => (
                      <div key={i} className="p-4 bg-slate-500/10 rounded-lg mb-2 border border-black/10">
                        <div className="flex justify-between mb-2">
                          <div><span className="font-medium text-slate-900 font-bold">{c.course}</span><span className="text-slate-400 mx-2">&middot;</span><span className="text-sm text-slate-500">{c.category}</span></div>
                          <span className="font-semibold text-amber-600">{c.percentage}%</span>
                        </div>
                        <Progress value={c.percentage} className="h-2" />
                        <p className="text-xs text-slate-500 mt-1">{c.completed} of {c.total} classes completed</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Assignment Summary */}
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-900 font-bold mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-violet-600" /> Assignment Summary</h3>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="text-center p-3 bg-slate-500/10 rounded-lg border border-black/10"><div className="text-xl font-bold text-slate-900 font-bold">{viewingReport.assignment_summary?.total || 0}</div><div className="text-xs text-slate-500">Total</div></div>
                    <div className="text-center p-3 bg-blue-500/10 rounded-lg border border-blue-100"><div className="text-xl font-bold text-blue-700">{viewingReport.assignment_summary?.submitted || 0}</div><div className="text-xs text-blue-600">Submitted</div></div>
                    <div className="text-center p-3 bg-emerald-500/10 rounded-lg border border-emerald-100"><div className="text-xl font-bold text-emerald-700">{viewingReport.assignment_summary?.graded || 0}</div><div className="text-xs text-emerald-600">Graded</div></div>
                    <div className="text-center p-3 bg-amber-500/10 rounded-lg border border-amber-100"><div className="text-xl font-bold text-amber-700">{viewingReport.assignment_summary?.average_grade || 'N/A'}</div><div className="text-xs text-amber-600">Avg Grade</div></div>
                  </div>
                  {viewingReport.assignments?.length > 0 && (
                    <Table>
                      <TableHeader><TableRow className="bg-slate-500/10"><TableHead>Assignment</TableHead><TableHead>Status</TableHead><TableHead>Grade</TableHead><TableHead>Feedback</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {viewingReport.assignments.map((a, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{a.title}</TableCell>
                            <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.status === 'graded' ? 'bg-emerald-100 text-emerald-700' : a.status === 'submitted' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{a.status}</span></TableCell>
                            <TableCell className="font-semibold">{a.grade || '-'}</TableCell>
                            <TableCell className="text-sm text-slate-500">{a.feedback || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>

                {/* Teacher Remarks */}
                {viewingReport.teacher_remarks && (
                  <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-200">
                    <h3 className="font-semibold text-amber-900 mb-1 text-sm">Teacher Remarks</h3>
                    <p className="text-slate-700 font-semibold">{viewingReport.teacher_remarks}</p>
                    <p className="text-xs text-amber-600 mt-2">— {viewingReport.teacher_name}</p>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </main>
      {/* Right utility rail — Spark (bottom) */}
      <RightUtilityRail sparkMode="student">
        <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-slate-200">
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1">Parent Hub</p>
          <p className="text-sm font-semibold">Stay in the loop</p>
          <p className="text-xs text-slate-400 mt-1">Ask Spark to summarise this week's feedback or draft a note to the teacher.</p>
        </div>
      </RightUtilityRail>
    </div>
  );
};

export default ParentDashboard;
